import React, { FC, useState } from "react";
import { Box, IconButton, Menu, MenuItem, ListItemIcon, ListItemText } from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import AddIcon from "@mui/icons-material/Add";
import VerifiedIcon from "@mui/icons-material/Verified";
import MenuIcon from "@mui/icons-material/Menu";
import { BillingStepper } from "../../../../Base/components/Stepper";
import ValidationProgressBar from "./ValidationProgressBar";
import { ValidationProgress } from "../types/interface";
import { colors, spacing } from "../styles/tokens";

export interface GkvHeaderActionsProps {
  activeStep: number;
  hasBills: boolean;
  isValidating: boolean;
  validationProgress: ValidationProgress;
  onLoadAll: () => void;
  onAddRx: () => void;
  onFilterValid: () => void;
}

/**
 * Sticky header bar with a hamburger menu and a save button.
 *
 * Menu items:
 * - Alle laden (always visible)
 * - Rezept hinzufügen (visible when bills exist)
 * - nur Valide (visible when bills exist)
 */
const GkvHeaderActions: FC<GkvHeaderActionsProps> = ({
  activeStep,
  hasBills,
  isValidating,
  validationProgress,
  onLoadAll,
  onAddRx,
  onFilterValid,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMenuAction = (action: () => void) => {
    action();
    handleMenuClose();
  };

  return (
    <Box>
      {isValidating && <ValidationProgressBar progress={validationProgress} />}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {/* Stepper */}
        <Box sx={{ flex: 1 }}>
          <BillingStepper activeStep={activeStep} hideProgress />
        </Box>

        {/* Hamburger menu */}
        <IconButton
          onClick={handleMenuClick}
          size="small"
          sx={{
            backgroundColor: "rgba(255, 255, 255, 0.1)",
            color: colors.textOnDark,
            padding: 0.75,
            border: "1px solid rgba(255,255,255,0.2)",
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.2)",
            },
          }}
        >
          <MenuIcon fontSize="small" />
        </IconButton>
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
          <MenuItem onClick={() => handleMenuAction(onLoadAll)}>
            <ListItemIcon>
              <FileDownloadIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Alle laden</ListItemText>
          </MenuItem>
          {hasBills && (
            <MenuItem onClick={() => handleMenuAction(onAddRx)}>
              <ListItemIcon>
                <AddIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Rezept hinzufügen</ListItemText>
            </MenuItem>
          )}
          {hasBills && (
            <MenuItem onClick={() => handleMenuAction(onFilterValid)}>
              <ListItemIcon>
                <VerifiedIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>nur Valide</ListItemText>
            </MenuItem>
          )}
        </Menu>
      </Box>
    </Box>
  );
};

export default GkvHeaderActions;
